import { BulkEditSubItem, SubItem } from '../../types/sub-item.types';
import { useEffect, useMemo } from 'react';

import { HiddenCompositionSharePAs } from '../../state/column-hiding.state';
import { Item } from '../../types/item.types';
import { RowSelectionState } from '@tanstack/react-table';
import create from 'zustand';
import flow from 'lodash/fp/flow';
import forOwn from 'lodash/forOwn';
import fpGet from 'lodash/fp/get';
import get from 'lodash/get';
import groupBy from 'lodash/fp/groupBy';
import keys from 'lodash/keys';
import map from 'lodash/fp/map';
import shallow from 'zustand/shallow';
import uniq from 'lodash/fp/uniq';
import { useSubItemsFromSelections } from '../../state/selection.state';
import { useTableDataState } from '../../state/table-data.state';

type BulkEditState = {
   rowSelection: RowSelectionState;
   setRowSelection: (updater: any) => void;
   resetRowSelection: () => void;
   tableData: BulkEditSubItem[];
   selectedIds: Set<string>;
   setSelectedIds: (ids: Set<string>) => void;
};

const getBulkEditItemId = (item: BulkEditSubItem): string => {
   return `${item.partDetail}\x1F${item.part}\x1F${item.materialGrade}`;
};

export const useBulkEditState = create<BulkEditState>((set, get) => ({
   rowSelection: {},
   setRowSelection: (updater) => {
      const newRowSelection = updater(get().rowSelection);

      const tableData = get().tableData;
      const selectedIds = new Set<string>();

      Object.keys(newRowSelection).forEach((indexStr) => {
         const index = Number(indexStr);
         if (newRowSelection[indexStr] && tableData[index]) {
            selectedIds.add(getBulkEditItemId(tableData[index]));
         }
      });

      set({
         rowSelection: newRowSelection,
         selectedIds,
      });
   },
   resetRowSelection: () => {
      set({ rowSelection: {}, selectedIds: new Set() });
   },
   tableData: [],
   selectedIds: new Set(),
   setSelectedIds: (ids: Set<string>) => set({ selectedIds: ids }),
}));

// creates the bulk edit data for the table using the currently selected subitems from the main table
export const useCreateBulkEditTableData = () => {
   const selectedSubItems = useSubItemsFromSelections();
   const mainTableData = useTableDataState((state) => state.data);

   useEffect(() => {
      const previousSelectedIds = useBulkEditState.getState().selectedIds;

      const newTableData = flow(
         groupBy((s: SubItem) => `${s.partDetail}\x1F${s.part}\x1F${s.materialGrade}`),
         map((group) => {
            const differingFields: string[] = [];
            forOwn(group[0], function (value, key) {
               if (keys(groupBy((s: SubItem) => get(s, key), group)).length !== 1) {
                  differingFields.push(key);
               }
            });

            const parentId = group[0].parentId;
            const parentItem = mainTableData.find((item) => item.id === parentId);
            const parentPA = parentItem?.pa || '';

            const shouldHideCompositionShare = HiddenCompositionSharePAs.includes(parentPA);

            const bulkEditSubItem: BulkEditSubItem = {
               subItems: group,
               locked: group.filter((s) => s.locked).length,
               inactive: group.filter((s) => !s.active).length,
               areaDensity: differingFields.includes('areaDensity') ? '*' : group[0].areaDensity,
               clientCreated: differingFields.includes('clientCreated') ? false : group[0].clientCreated,
               comment: differingFields.includes('comment') ? '*' : group[0].comment,
               density: differingFields.includes('density') ? '*' : group[0].density,
               differingFields: differingFields,
               mass: differingFields.includes('mass') ? '*' : group[0].mass,
               part: differingFields.includes('part') ? '*' : group[0].part,
               partDetail: differingFields.includes('partDetail') ? '*' : group[0].partDetail,
               quantity: differingFields.includes('quantity') ? '*' : group[0].quantity,
               materialGradeId: differingFields.includes('materialGradeId') ? '*' : group[0].materialGradeId,
               materialGrade: differingFields.includes('materialGrade') ? '*' : group[0].materialGrade,
               recycledContent: differingFields.includes('recycledContent') ? '*' : group[0].recycledContent,
               renewableContent: differingFields.includes('renewableContent') ? '*' : group[0].renewableContent,
               threeDModelNo: differingFields.includes('threeDModelNo') ? '*' : group[0].threeDModelNo,
               volume: differingFields.includes('volume') ? '*' : group[0].volume,
               compositionShare: shouldHideCompositionShare
                  ? undefined
                  : differingFields.includes('compositionShare')
                     ? '*'
                     : group[0].compositionShare,
            };
            return bulkEditSubItem;
         })
      )(selectedSubItems);

      const newRowSelection: RowSelectionState = {};
      const newSelectedIds = new Set<string>();

      newTableData.forEach((item: BulkEditSubItem, index: number) => {
         const itemId = getBulkEditItemId(item);
         if (previousSelectedIds.has(itemId)) {
            newRowSelection[index] = true;
            newSelectedIds.add(itemId);
         }
      });

      useBulkEditState.setState({
         tableData: newTableData,
         rowSelection: newRowSelection,
         selectedIds: newSelectedIds,
      });
   }, [selectedSubItems, mainTableData]);
};

// gets the item numbers which correspond to the selected sub items in the bulk edit table
export const useParentItemsFromBulkEditSelection = () => {
   const [selection, data] = useBulkEditState((state) => [state.rowSelection, state.tableData], shallow);
   const mainTableData = useTableDataState((state) => state.data);

   return useMemo(() => {
      if (!selection || !data?.length || !mainTableData?.length) {
         return [];
      }

      const parentIds = new Set<string>();

      Object.keys(selection).forEach((indexStr) => {
         const index = Number(indexStr);
         const bulkItem = data[index];

         if (bulkItem?.subItems) {
            bulkItem.subItems.filter((s) => !s.locked && s.parentId).forEach((s) => parentIds.add(s.parentId.toString()));
         }
      });

      if (parentIds.size === 0) {
         return [];
      }

      const parentItems: Item[] = [];
      const foundIds = new Set<string>();

      mainTableData.forEach((item) => {
         if (!item?.itemNo) return;

         const itemNoStr = item.itemNo.toString();

         if (parentIds.has(itemNoStr) && !foundIds.has(itemNoStr)) {
            parentItems.push(item);
            foundIds.add(itemNoStr);
         }
      });

      return parentItems;
   }, [selection, data, mainTableData]);
};

export const useGetBulkEditItemsFromSelection = () => {
   const [selection, data] = useBulkEditState((state) => [state.rowSelection, state.tableData], shallow);

   const bulkEditSubItems = useMemo(() => {
      if (!data || !selection) return [];

      return data.filter((bulkEditSubItem, itemIndex) => fpGet(itemIndex, selection) && bulkEditSubItem?.subItems?.length > 0);
   }, [selection, data]);

   const combinedDifferingFields = useMemo(() => {
      if (!bulkEditSubItems.length) return [];

      const differingFields: string[] = [];
      const firstItem = bulkEditSubItems[0];

      if (bulkEditSubItems.length > 1) {
         Object.keys(firstItem || {})
            .filter((key) => key !== 'subItems' && key !== 'differingFields')
            .forEach((key) => {
               if (uniq(bulkEditSubItems.map((item) => get(item, key))).length > 1) {
                  differingFields.push(key);
               }
            });
      }

      bulkEditSubItems.forEach((item) => {
         if (Array.isArray(item?.differingFields)) {
            differingFields.push(...item.differingFields);
         }
      });

      return uniq(differingFields);
   }, [bulkEditSubItems]);

   const onlyLockedSubItems = useMemo(() => {
      if (!bulkEditSubItems.length) return false;

      return !bulkEditSubItems.some((bulkItem) => bulkItem?.subItems?.some((subItem) => !subItem.locked));
   }, [bulkEditSubItems]);

   return { bulkEditSubItems, combinedDifferingFields, onlyLockedSubItems };
};
